import { useAuthContext } from '@aibees/shared/auth';
import {
  Icon,
  IconButton,
  PopoverListButtonItem,
  usePopover,
} from '@aibees/shared/components';
import { Member, MemberStatus, MemberType } from '@aibees/shared/definition';
import { notificationIconMap } from '@aibees/shared/features/notifications/config';
import { useCancelTransferMutationApi } from '@aibees/shared/features/user-profile/api/use-cancel-transfer-mutation-api';
import { useResendInvitationEmailApi } from '@aibees/shared/features/user-profile/api/use-resend-invitation-email-api';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils/class-names';
import { useCancelInviteMember } from '@aibees/shared/views/user-management-view/api/use-cancel-invite-member';
import { useEditMember } from '@aibees/shared/views/user-management-view/api/use-edit-member';
import { ChangeOwnershipDialog } from '@aibees/shared/views/user-management-view/components/change-ownership-dialog';
import { CreditCapDialog } from '@aibees/shared/views/user-management-view/components/credit-cap-dialog';
import { DeactivateMemberDialog } from '@aibees/shared/views/user-management-view/components/deactivate-member-dialog';
import { DeleteMemberDialog } from '@aibees/shared/views/user-management-view/components/delete-member-dialog';
import { EditRoleDialog } from '@aibees/shared/views/user-management-view/components/edit-role-dialog';
import { EditRoleDialogProvider } from '@aibees/shared/views/user-management-view/components/edit-role-dialog/context';
import { useTeamMemberActionConfig } from '@aibees/shared/views/user-management-view/context/team-member-action-config';
import {
  useGetUserRole,
  useUpdateWorkspace,
  useWorkspace,
} from '@aibees/shared/workspace';
import { Tooltip } from '@mui/material';
import { random } from 'lodash-es';
import { memo, useMemo, useState } from 'react';
import * as Styled from './styles';
import {
  TeamMemberActionType as ActionType,
  TeamMemberEditActionProps as Props,
} from './types';
import { filterMemberEditItems } from './utils';

const ANCHOR_ORIGIN = {
  horizontal: 'right' as const,
  vertical: 'bottom' as const,
};
const TRANSFORM_ORIGIN = {
  horizontal: 'right' as const,
  vertical: 'top' as const,
};

export const TeamMemberEditAction = memo(({ className, item }: Props) => {
  const popover = usePopover();
  const [selectedItem, setSelectedItem] = useState<ActionType | undefined>();
  const deselectItem = () => setSelectedItem(undefined);
  const role = useGetUserRole();
  const notify = useNotify();
  const editMemberAPI = useEditMember(item.user?.id);
  const updateWorkspace = useUpdateWorkspace();
  const cancelInviteAPI = useCancelInviteMember(item.user?.id);
  const workspace = useWorkspace();
  const cancelTransferAPI = useCancelTransferMutationApi();
  const resendInvitationEmailAPI = useResendInvitationEmailApi(item.user?.id);
  const { user } = useAuthContext();
  const { inCurrentPlan } = useTeamMemberActionConfig();
  const isDisabled = inCurrentPlan;

  const popoverItems = useMemo(
    () => filterMemberEditItems(item, role, workspace, user),
    [item, role, workspace, user]
  );

  const activateMember = (data: Member) => {
    editMemberAPI
      .mutateAsync({
        member: { ...data, ...{ status: MemberStatus.Active } },
      })
      .then((updatedWorkspace) => {
        updateWorkspace(updatedWorkspace);
        notify.complete(
          'User activated!',
          `The user ${item.user?.firstName} ${item.user?.lastName} has been successfully activated`,
          random(1, 100),
          {
            icon: notificationIconMap.UserManagement!,
          }
        );
      });
  };

  const cancelInviteMember = () => {
    cancelInviteAPI
      .mutateAsync({})
      .then((updatedWorkspace) => {
        updateWorkspace(updatedWorkspace);
        notify.warning(
          'Invite member cancelled!',
          `The user ${item.user?.firstName} ${item.user?.lastName} has been successfully cancelled`,
          random(1, 100),
          {
            icon: notificationIconMap.UserManagement!,
          }
        );
      })
      .catch((error) => {
        notify.error('Failed to cancel invite. Please try again.');
      });
  };

  const cancelTransferOwnership = () => {
    cancelTransferAPI.mutateAsync({}).then((updatedWorkspace) => {
      notify.complete(
        'Ownership successfully cancelled',
        `Ownership transfer to <b>${item.user?.firstName} ${item.user?.lastName}</b> has been cancelled.`,
        random(1, 100),
        {
          icon: notificationIconMap.UserManagement!,
        }
      );

      updateWorkspace(updatedWorkspace);
    });
  };

  const resendInvitationEmail = () => {
    resendInvitationEmailAPI
      .mutateAsync({})
      .then(() => {
        notify.success('The invitation email has been sent.');
      })
      .catch(() => {
        notify.error('An error occured.');
      });
  };

  const onSelect = (selected: PopoverListButtonItem) => {
    if (selected.key === ActionType.activate) {
      activateMember(item);
    } else if (selected.key === ActionType.cancelInvite) {
      cancelInviteMember();
    } else if (selected.key === ActionType.cancelTransfer) {
      cancelTransferOwnership();
    } else if (selected.key === ActionType.resend) {
      resendInvitationEmail();
    } else {
      setSelectedItem(selected.key as ActionType);
    }
  };

  const tooltipTitle = useMemo(() => {
    if (popoverItems.length < 1 && item.status !== MemberStatus.Deleted) {
      return role === MemberType.User
        ? 'Access Denied'
        : 'Access Denied: Contact Owner.';
    }
    return '';
  }, [role, popoverItems.length, item]);

  return (
    <>
      {popoverItems.length < 1 ? (
        <Tooltip title={tooltipTitle} placement="left">
          <Icon name="more-horizontal" size={24} color="grey.300" />
        </Tooltip>
      ) : (
        <Styled.Wrapper
          className={classnames('TeamMemberEditAction', className)}
          items={popoverItems}
          state={popover}
          variant="soft"
          onSelect={onSelect}
          anchorOrigin={ANCHOR_ORIGIN}
          transformOrigin={TRANSFORM_ORIGIN}
          buttonElement={
            <IconButton
              icon={
                <Icon
                  name="more-horizontal"
                  size={24}
                  color={popover.open ? 'primary.400' : 'grey.500'}
                />
              }
              color="default"
              variant="text"
              disabled={isDisabled}
            />
          }
        />
      )}

      {selectedItem === ActionType.editRole && (
        <EditRoleDialogProvider>
          <EditRoleDialog item={item} onClose={deselectItem} />
        </EditRoleDialogProvider>
      )}

      {selectedItem === ActionType.deactivate && (
        <DeactivateMemberDialog
          item={item}
          onClose={deselectItem}
          status={MemberStatus.disable}
        />
      )}

      {selectedItem === ActionType.reactivate && (
        <DeactivateMemberDialog
          item={item}
          onClose={deselectItem}
          status={MemberStatus.Active}
        />
      )}

      {selectedItem === ActionType.delete && (
        <DeleteMemberDialog item={item} onClose={deselectItem} />
      )}
      {selectedItem === ActionType.changeOwnership && (
        <ChangeOwnershipDialog item={item} onClose={deselectItem} />
      )}
      {selectedItem === ActionType.creditCap && (
        <CreditCapDialog item={item} onClose={deselectItem} />
      )}
    </>
  );
});

TeamMemberEditAction.displayName = 'TeamMemberEditAction';
